import React, { useContext, useMemo, useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { gql, useMutation, useQuery } from "@apollo/client";
import { Dropdown } from "react-bootstrap";
import DataTable from "react-data-table-component";
import "bootstrap/dist/css/bootstrap.min.css";
import EntityLoadingState from "../../components/EntityLoadingState";
import Paginator from "../../components/Paginator";
import useListLayout from "../../hooks/useListLayout";
import StarekContext from "../../context/StarekContext";
import {
    isBitSet,
    setBit,
    clearBit,
    resolveEntityKindBitLocations,
} from "../../utils";
import "../../starek.css";

const ENTITY_TYPES_QUERY = gql`
    query EntityType {
        entityTypes {
            id
            code
            singular
            plural
            type
            description
            is_active
        }
    }
`;

const USER_NOTIFICATIONS_QUERY = gql`
    query GetUserNotifications($userId: Int!) {
        userNotifications(where: { user_id: { equals: $userId } }) {
            id
            user_id
            entity_type_id
            actionMask
            channelMask
            enabled
            entity_type {
                id
                code
            }
        }
    }
`;

const USER_PHONE_QUERY = gql`
    query GetUserPhone($where: UserWhereUniqueInput!) {
        user(where: $where) {
            id
            phone
        }
    }
`;

const UPDATE_MANY_USER_NOTIFICATION = gql`
    mutation UpdateManyUserNotification(
        $where: UserNotificationWhereInput!
        $data: UserNotificationUpdateManyMutationInput!
    ) {
        updateManyUserNotification(where: $where, data: $data) {
            count
        }
    }
`;

const CREATE_ONE_USER_NOTIFICATION = gql`
    mutation CreateOneUserNotification($data: UserNotificationCreateInput!) {
        createOneUserNotification(data: $data) {
            id
        }
    }
`;

const ENTITY_TYPE = {
    ENTITY: 1,
    ASSOCIATION: 2,
    ACTION: 4,
};

const ACTION_LABELS = {
    READ: "Read",
    WRITE: "Write",
    DESTROY: "Destroy",
    EXECUTE: "Execute",
    LINK: "Link",
    UNLINK: "Unlink",
};

const ACTION_BIT_KEY_ALIASES = {
    READ: ["BIT_LOCATION_READ"],
    WRITE: ["BIT_LOCATION_WRITE"],
    DESTROY: ["BIT_LOCATION_DESTROY", "BIT_LOCATION_DELETE"],
    EXECUTE: ["BIT_LOCATION_EXECUTE"],
    LINK: ["BIT_LOCATION_LINK"],
    UNLINK: ["BIT_LOCATION_UNLINK"],
};

const ACTION_KEYS_BY_ENTITY_TYPE = {
    [ENTITY_TYPE.ENTITY]: ["READ", "WRITE", "DESTROY"],
    [ENTITY_TYPE.ASSOCIATION]: ["LINK", "UNLINK"],
    [ENTITY_TYPE.ACTION]: ["EXECUTE"],
};

const CHANNEL_LABELS = {
    EMAIL: "Email",
    SMS: "SMS",
    WEBHOOK: "Webhook",
    IN_APP: "In App",
    PUSH: "Push",
};

const CHANNEL_BIT_KEY_ALIASES = {
    EMAIL: ["BIT_LOCATION_EMAIL"],
    SMS: ["BIT_LOCATION_SMS"],
    WEBHOOK: ["BIT_LOCATION_WEBHOOK"],
    IN_APP: ["BIT_LOCATION_IN_APP", "BIT_LOCATION_INAPP"],
    PUSH: ["BIT_LOCATION_PUSH"],
};

function resolveActionEntityType(typeValue, entityKindBits) {
    if (isBitSet(typeValue, entityKindBits.entity)) return ENTITY_TYPE.ENTITY;
    if (isBitSet(typeValue, entityKindBits.association)) {
        return ENTITY_TYPE.ASSOCIATION;
    }
    if (isBitSet(typeValue, entityKindBits.action)) return ENTITY_TYPE.ACTION;
    return null;
}

function toTitleCase(text) {
    if (!text) return "";
    return String(text)
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase());
}

function normalizeEntityName(entityType) {
    return (
        entityType?.singular ||
        entityType?.plural ||
        toTitleCase(entityType?.code || "Unknown")
    );
}

function toArray(value) {
    if (Array.isArray(value)) return value;
    if (!value || typeof value !== "object") return [];
    if (Array.isArray(value.items)) return value.items;
    return Object.values(value).filter(
        (item) => item && typeof item === "object",
    );
}

function extractNotifierBitLocation(types) {
    const directCandidates = [
        types?.entity_kind?.flags?.BIT_LOCATION_NOTIFIER,
        types?.entity_kind?.flags?.BIT_LCOATION_NOTIFIER,
        types?.entity_kind?.BIT_LOCATION_NOTIFIER,
        types?.entity_kind?.BIT_LCOATION_NOTIFIER,
        types?.entityKind?.flags?.BIT_LOCATION_NOTIFIER,
        types?.entityKind?.flags?.BIT_LCOATION_NOTIFIER,
        types?.entityKind?.BIT_LOCATION_NOTIFIER,
        types?.entityKind?.BIT_LCOATION_NOTIFIER,
        types?.entity_type?.entity_kind_flags?.BIT_LOCATION_NOTIFIER,
        types?.entity_type?.entity_kind_flags?.BIT_LCOATION_NOTIFIER,
        types?.entityType?.entity_kind_flags?.BIT_LOCATION_NOTIFIER,
        types?.entityType?.entity_kind_flags?.BIT_LCOATION_NOTIFIER,
    ];

    for (const candidate of directCandidates) {
        if (Number.isInteger(candidate)) return candidate;
        if (typeof candidate === "string" && candidate.trim() !== "") {
            const parsed = Number(candidate);
            if (Number.isFinite(parsed)) return parsed;
        }
    }

    const queue = [types];
    const visited = new Set();

    while (queue.length) {
        const current = queue.shift();
        if (!current || typeof current !== "object") continue;
        if (visited.has(current)) continue;
        visited.add(current);

        const fromStandard = current.BIT_LOCATION_NOTIFIER;
        if (Number.isInteger(fromStandard)) return fromStandard;
        if (typeof fromStandard === "string" && fromStandard.trim() !== "") {
            const parsed = Number(fromStandard);
            if (Number.isFinite(parsed)) return parsed;
        }

        const fromTypo = current.BIT_LCOATION_NOTIFIER;
        if (Number.isInteger(fromTypo)) return fromTypo;
        if (typeof fromTypo === "string" && fromTypo.trim() !== "") {
            const parsed = Number(fromTypo);
            if (Number.isFinite(parsed)) return parsed;
        }

        for (const value of Object.values(current)) {
            if (value && typeof value === "object") {
                queue.push(value);
            }
        }
    }

    return null;
}

function extractEntityKindMask(entityTypeMeta) {
    const raw =
        entityTypeMeta?.entity_kind ??
        entityTypeMeta?.entityKind ??
        entityTypeMeta?.kind ??
        entityTypeMeta?.entity_kind_mask ??
        entityTypeMeta?.entityKindMask;

    if (typeof raw === "number") return raw;
    if (typeof raw === "string" && raw.trim() !== "") {
        const parsed = Number(raw);
        return Number.isFinite(parsed) ? parsed : null;
    }
    if (raw && typeof raw === "object") {
        const objectValue =
            raw.value ?? raw.mask ?? raw.bits ?? raw.bitmask ?? raw.id;
        if (typeof objectValue === "number") return objectValue;
        if (typeof objectValue === "string" && objectValue.trim() !== "") {
            const parsed = Number(objectValue);
            return Number.isFinite(parsed) ? parsed : null;
        }
    }

    return null;
}

function buildNotifierEnabledCodeSet(types) {
    const bitLocation = extractNotifierBitLocation(types);
    if (bitLocation == null) return new Set();

    const allEntityMeta = [
        ...toArray(types?.entity_type),
        ...toArray(types?.entityType),
        ...toArray(types?.entity_types),
        ...toArray(types?.entityTypes),
    ];

    if (!allEntityMeta.length) return new Set();

    const enabled = new Set();

    for (const meta of allEntityMeta) {
        const code = String(meta?.code || meta?.name || meta?.key || "")
            .trim()
            .toLowerCase();
        if (!code) continue;

        const explicitNotifier =
            meta?.is_notifier ??
            meta?.isNotifier ??
            meta?.notifier ??
            meta?.supports_notifier ??
            meta?.supportsNotifier;

        if (typeof explicitNotifier === "boolean") {
            if (explicitNotifier) enabled.add(code);
            continue;
        }

        const kindMask = extractEntityKindMask(meta);
        if (kindMask == null) continue;

        const notifierEnabled = isBitSet(kindMask, bitLocation);
        if (notifierEnabled) enabled.add(code);
    }

    return enabled;
}

function toNumericBitLocation(value) {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim() !== "") {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) return parsed;
    }
    return null;
}

function findBitLocationByKeys(types, candidateKeys) {
    const knownFlagContainers = [
        types?.notification?.flags,
        types?.notifications?.flags,
        types?.user_notification?.flags,
        types?.user_notifications?.flags,
        types?.userNotification?.flags,
        types?.userNotifications?.flags,
        types?.channel?.flags,
        types?.channels?.flags,
        types?.channel_mask?.flags,
        types?.channelMask?.flags,
        types?.entity_type?.channel_flags,
        types?.entityType?.channel_flags,
        types?.entity_type?.channelMask?.flags,
        types?.entityType?.channelMask?.flags,
    ];

    for (const container of knownFlagContainers) {
        if (!container || typeof container !== "object") continue;
        for (const key of candidateKeys) {
            const location = toNumericBitLocation(container[key]);
            if (location != null) return location;
        }
    }

    const queue = [types];
    const visited = new Set();

    while (queue.length) {
        const current = queue.shift();
        if (!current || typeof current !== "object") continue;
        if (visited.has(current)) continue;
        visited.add(current);

        for (const key of candidateKeys) {
            const location = toNumericBitLocation(current[key]);
            if (location != null) return location;
        }

        for (const value of Object.values(current)) {
            if (value && typeof value === "object") queue.push(value);
        }
    }

    return null;
}

function buildChannelOptionsFromTypes(types) {
    const options = [];

    for (const [channelName, aliases] of Object.entries(
        CHANNEL_BIT_KEY_ALIASES,
    )) {
        const bitLocation = findBitLocationByKeys(types, aliases);
        if (bitLocation == null) continue;

        options.push({
            key: channelName.toLowerCase(),
            label: CHANNEL_LABELS[channelName] || toTitleCase(channelName),
            bitLocation,
            mask: setBit(0, bitLocation),
        });
    }

    return options;
}

function buildActionOptionsByEntityType(types) {
    const optionsByAction = {};

    for (const [actionKey, aliases] of Object.entries(ACTION_BIT_KEY_ALIASES)) {
        const bitLocation = findBitLocationByKeys(types, aliases);
        if (bitLocation == null) continue;

        optionsByAction[actionKey] = {
            key: actionKey.toLowerCase(),
            label: ACTION_LABELS[actionKey] || toTitleCase(actionKey),
            bitLocation,
            mask: setBit(0, bitLocation),
        };
    }

    return {
        [ENTITY_TYPE.ENTITY]: ACTION_KEYS_BY_ENTITY_TYPE[ENTITY_TYPE.ENTITY]
            .map((actionKey) => optionsByAction[actionKey])
            .filter(Boolean),
        [ENTITY_TYPE.ASSOCIATION]: ACTION_KEYS_BY_ENTITY_TYPE[
            ENTITY_TYPE.ASSOCIATION
        ]
            .map((actionKey) => optionsByAction[actionKey])
            .filter(Boolean),
        [ENTITY_TYPE.ACTION]: ACTION_KEYS_BY_ENTITY_TYPE[ENTITY_TYPE.ACTION]
            .map((actionKey) => optionsByAction[actionKey])
            .filter(Boolean),
    };
}

function ToggleSwitch({ id, checked, onChange, label }) {
    return (
        <div className="d-flex align-items-center">
            <div
                className="form-check form-switch mb-0"
                style={{ position: "relative" }}
            >
                <input
                    id={id}
                    type="checkbox"
                    className="form-check-input"
                    checked={checked}
                    onChange={onChange}
                    style={{
                        backgroundColor: checked ? "#0d6efd" : "#6c757d",
                        borderColor: checked ? "#0d6efd" : "#6c757d",
                        width: "2rem",
                        height: "1rem",
                        marginTop: 0,
                        borderRadius: "0.5rem",
                    }}
                />
            </div>
            {label ? (
                <label
                    className="form-check-label d-flex align-items-center mb-0"
                    htmlFor={id}
                    style={{ marginLeft: "6px", fontSize: "13px", lineHeight: "1rem" }}
                >
                    {label}
                </label>
            ) : null}
        </div>
    );
}

export function Notifications() {
    const starekContext = useContext(StarekContext);
    const userId = Number.parseInt(String(starekContext?.user?.id || ""), 10);

    const [rowsByEntity, setRowsByEntity] = useState({});
    const [dirtyEntityIds, setDirtyEntityIds] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [filterText, setFilterText] = useState("");
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [showColumns, setShowColumns] = useState([true, true]);
    // "Entity Type" column sort; rows default to A-Z by entity name.
    const [sortDirection, setSortDirection] = useState("asc");

    // Paged by the user's list layout like the other lists, but on the client: the rows are the
    // notifier entity types (a bitmask on EntityType.type picks them, which a Prisma
    // where cannot express) merged with this user's preferences, and unsaved edits
    // and the All/No bulk buttons span every row, not just one page.
    const { pageSize } = useListLayout();
    const [searchParams, setSearchParams] = useSearchParams();
    const requestedPage = Math.max(
        1,
        parseInt(searchParams.get("page") || "1", 10) || 1,
    );
    const setPage = (next) =>
        setSearchParams(
            (prev) => {
                const params = new URLSearchParams(prev);
                if (next > 1) params.set("page", String(next));
                else params.delete("page");
                return params;
            },
            { replace: true },
        );

    const {
        data: entityTypesData,
        loading: loadingEntityTypes,
        error: entityTypesError,
    } = useQuery(ENTITY_TYPES_QUERY, {
        client: starekContext.engineClient,
        skip: !starekContext.engineClient,
    });

    const {
        data: notificationsData,
        loading: loadingNotifications,
        error: notificationsError,
        refetch,
    } = useQuery(USER_NOTIFICATIONS_QUERY, {
        client: starekContext.engineClient,
        variables: { userId },
        skip: !starekContext.engineClient || !userId,
        fetchPolicy: "cache-and-network",
    });

    const { data: userPhoneData } = useQuery(USER_PHONE_QUERY, {
        client: starekContext.engineClient,
        variables: { where: { id: userId } },
        skip: !starekContext.engineClient || !userId,
        fetchPolicy: "cache-and-network",
    });

    const [updateManyUserNotification] = useMutation(
        UPDATE_MANY_USER_NOTIFICATION,
        {
            client: starekContext.engineClient,
        },
    );

    const [createOneUserNotification] = useMutation(
        CREATE_ONE_USER_NOTIFICATION,
        {
            client: starekContext.engineClient,
        },
    );

    const channelOptions = useMemo(
        () => buildChannelOptionsFromTypes(starekContext?.types),
        [starekContext?.types],
    );

    const actionOptionsByEntityType = useMemo(
        () => buildActionOptionsByEntityType(starekContext?.types),
        [starekContext?.types],
    );

    const entityKindBits = useMemo(
        () => resolveEntityKindBitLocations(starekContext?.types),
        [starekContext?.types],
    );

    const hasAnyActionOptions = useMemo(
        () =>
            Object.values(actionOptionsByEntityType).some(
                (options) => Array.isArray(options) && options.length > 0,
            ),
        [actionOptionsByEntityType],
    );

    const entityTypes = useMemo(() => {
        const all = entityTypesData?.entityTypes || [];
        const bitLocation = extractNotifierBitLocation(starekContext?.types);

        return all.filter((entityType) => {
            if (!entityType?.is_active) return false;
            return isBitSet(entityType?.type, bitLocation);
        });
    }, [entityTypesData, starekContext?.types]);

    useEffect(() => {
        if (!entityTypes.length) return;

        const allNotifications = notificationsData?.userNotifications || [];
        const grouped = {};

        for (const entityType of entityTypes) {
            const entityId = Number(entityType.id);
            const current = allNotifications.filter(
                (notification) =>
                    Number(notification.entity_type_id) === entityId,
            );

            const actionMask = current.length
                ? current.reduce(
                      (accumulator, notification) =>
                          accumulator | (notification.actionMask || 0),
                      0,
                  )
                : 0;

            const channelMask = current.length
                ? current.reduce(
                      (accumulator, notification) =>
                          accumulator | (notification.channelMask || 0),
                      0,
                  )
                : 0;

            grouped[entityId] = {
                entityType,
                actionMask,
                channelMask,
            };
        }

        setRowsByEntity(grouped);
        setDirtyEntityIds({});
    }, [entityTypes, notificationsData]);

    const rows = useMemo(() => {
        return Object.values(rowsByEntity).sort((a, b) => {
            const left = normalizeEntityName(a.entityType);
            const right = normalizeEntityName(b.entityType);
            return left.localeCompare(right);
        });
    }, [rowsByEntity]);

    const smsChannel = useMemo(
        () => channelOptions.find((channel) => channel.key === "sms"),
        [channelOptions],
    );

    // SMS can only be delivered once the user has saved a phone number on their profile.
    const smsEnabledWithoutPhone = useMemo(() => {
        if (!smsChannel) return false;
        if (userPhoneData?.user?.phone) return false;
        return rows.some((row) =>
            isBitSet(row.channelMask, smsChannel.bitLocation),
        );
    }, [smsChannel, userPhoneData, rows]);

    const filteredRows = useMemo(() => {
        if (!filterText.trim()) return rows;

        const normalizedFilter = filterText.toLowerCase().trim();

        return rows.filter((row) => {
            const name = normalizeEntityName(row.entityType).toLowerCase();
            const code = String(row.entityType?.code || "").toLowerCase();
            return (
                name.includes(normalizedFilter) ||
                code.includes(normalizedFilter)
            );
        });
    }, [rows, filterText]);

    const sortedRows = useMemo(
        () =>
            sortDirection === "desc" ? [...filteredRows].reverse() : filteredRows,
        [filteredRows, sortDirection],
    );

    const pageCount = Math.max(1, Math.ceil(sortedRows.length / pageSize));
    const page = Math.min(requestedPage, pageCount);
    const pageRows = useMemo(
        () => sortedRows.slice((page - 1) * pageSize, page * pageSize),
        [sortedRows, page, pageSize],
    );

    // Back to page 1 when the search or the sort changes (not on the first render, so
    // a deep link to ?page=N is honoured).
    const pageResetKey = `${filterText}|${sortDirection}`;
    const lastPageResetKey = useRef(pageResetKey);
    useEffect(() => {
        if (lastPageResetKey.current === pageResetKey) return;
        lastPageResetKey.current = pageResetKey;
        if (requestedPage !== 1) setPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageResetKey]);

    const setEntityDraft = (entityTypeId, updater) => {
        setRowsByEntity((previous) => {
            const existing = previous[entityTypeId];
            if (!existing) return previous;

            return {
                ...previous,
                [entityTypeId]: updater(existing),
            };
        });

        setDirtyEntityIds((previous) => ({
            ...previous,
            [entityTypeId]: true,
        }));
    };

    const toggleMaskBit = (entityTypeId, field, bitLocation) => {
        setEntityDraft(entityTypeId, (row) => {
            const currentValue = row[field] || 0;
            const nextValue = isBitSet(currentValue, bitLocation)
                ? clearBit(currentValue, bitLocation)
                : setBit(currentValue, bitLocation);

            return {
                ...row,
                [field]: nextValue,
            };
        });
    };

    const setMaskForEntity = (entityTypeId, field, options, useAll) => {
        setEntityDraft(entityTypeId, (row) => {
            const allMask = options.reduce(
                (accumulator, option) => accumulator | option.mask,
                0,
            );

            return {
                ...row,
                [field]: useAll ? allMask : 0,
            };
        });
    };

    const getAllMask = (options) =>
        options.reduce((accumulator, option) => accumulator | option.mask, 0);

    const isAllSelected = (value, options) => {
        const allMask = getAllMask(options);
        if (!allMask) return false;
        return (value & allMask) === allMask;
    };

    const toggleAllNoneForEntity = (
        entityTypeId,
        field,
        options,
        currentValue,
    ) => {
        const allSelected = isAllSelected(currentValue || 0, options);
        setMaskForEntity(entityTypeId, field, options, !allSelected);
    };

    const applyChannelBulk = (useAll) => {
        const allChannelMask = channelOptions.reduce(
            (accumulator, option) => accumulator | option.mask,
            0,
        );

        setRowsByEntity((previous) => {
            const next = {};
            for (const [entityId, row] of Object.entries(previous)) {
                next[entityId] = {
                    ...row,
                    channelMask: useAll ? allChannelMask : 0,
                };
            }
            return next;
        });

        setDirtyEntityIds((previous) => {
            const next = { ...previous };
            for (const entityId of Object.keys(rowsByEntity)) {
                next[entityId] = true;
            }
            return next;
        });
    };

    const applyActionBulk = (useAll) => {
        setRowsByEntity((previous) => {
            const next = {};
            for (const [entityId, row] of Object.entries(previous)) {
                const actionEntityType = resolveActionEntityType(
                    row.entityType.type,
                    entityKindBits,
                );
                const actionOptions =
                    actionOptionsByEntityType[actionEntityType] || [];
                const allActionMask = actionOptions.reduce(
                    (accumulator, option) => accumulator | option.mask,
                    0,
                );

                next[entityId] = {
                    ...row,
                    actionMask: useAll ? allActionMask : 0,
                };
            }
            return next;
        });

        setDirtyEntityIds((previous) => {
            const next = { ...previous };
            for (const entityId of Object.keys(rowsByEntity)) {
                next[entityId] = true;
            }
            return next;
        });
    };

    const handleSave = async () => {
        try {
            if (!userId) {
                throw new Error("No authenticated user found.");
            }

            const changedEntityIds = Object.keys(dirtyEntityIds)
                .filter((entityId) => dirtyEntityIds[entityId])
                .map((entityId) => Number(entityId));

            if (!changedEntityIds.length) {
                starekContext.showInfo("No notification changes to save.");
                return;
            }

            setIsSaving(true);

            for (const entityTypeId of changedEntityIds) {
                const row = rowsByEntity[entityTypeId];
                if (!row) continue;
                const isEnabled =
                    Number(row.actionMask || 0) > 0 &&
                    Number(row.channelMask || 0) > 0;

                const updateResult = await updateManyUserNotification({
                    variables: {
                        where: {
                            AND: [
                                { user_id: { equals: userId } },
                                {
                                    entity_type_id: {
                                        equals: Number(entityTypeId),
                                    },
                                },
                            ],
                        },
                        data: {
                            actionMask: { set: row.actionMask || 0 },
                            channelMask: { set: row.channelMask || 0 },
                            enabled: { set: isEnabled },
                        },
                    },
                });

                const updatedCount =
                    updateResult?.data?.updateManyUserNotification?.count || 0;

                if (updatedCount === 0) {
                    await createOneUserNotification({
                        variables: {
                            data: {
                                user: { connect: { id: userId } },
                                entity_type: {
                                    connect: { id: Number(entityTypeId) },
                                },
                                actionMask: row.actionMask || 0,
                                channelMask: row.channelMask || 0,
                                enabled: isEnabled,
                            },
                        },
                    });
                }
            }

            await refetch();
            starekContext.showSuccess("Notification preferences updated.");
        } catch (error) {
            starekContext.showError(
                error?.message || "Failed to update notification preferences.",
            );
        } finally {
            setIsSaving(false);
        }
    };

    const toggleColumnVisibility = (index) => {
        setShowColumns((previous) => {
            const next = [...previous];
            next[index] = !next[index];
            return next;
        });
    };

    const columns = [
        {
            name: "Entity Type",
            grow: 1.6,
            sortable: true,
            selector: (row) => normalizeEntityName(row.entityType),
            cell: (row) => (
                <div>
                    <div className="text-dark" style={{ fontWeight: 500 }}>
                        {normalizeEntityName(row.entityType)}
                    </div>
                    <div className="text-muted small">
                        {row.entityType.code}
                    </div>
                </div>
            ),
        },
        {
            name: "Actions",
            grow: 2.3,
            cell: (row) => {
                const entityTypeId = Number(row.entityType.id);
                const actionEntityType = resolveActionEntityType(
                    row.entityType.type,
                    entityKindBits,
                );
                const actionOptions =
                    actionOptionsByEntityType[actionEntityType] || [];
                const allActionsSelected = isAllSelected(
                    row.actionMask || 0,
                    actionOptions,
                );

                return (
                    <div className="d-flex flex-column gap-1 py-1">
                        <div className="d-flex flex-wrap align-items-center gap-3">
                            {actionOptions.map((action) => (
                                <div key={`${entityTypeId}-${action.key}`}>
                                    <ToggleSwitch
                                        id={`action-${entityTypeId}-${action.key}`}
                                        checked={isBitSet(
                                            row.actionMask,
                                            action.bitLocation,
                                        )}
                                        onChange={() =>
                                            toggleMaskBit(
                                                entityTypeId,
                                                "actionMask",
                                                action.bitLocation,
                                            )
                                        }
                                        label={action.label}
                                    />
                                </div>
                            ))}
                            <button
                                type="button"
                                className={`btn btn-sm rounded-circle d-inline-flex align-items-center justify-content-center ${
                                    allActionsSelected
                                        ? "btn-primary"
                                        : "btn-dark"
                                }`}
                                title={
                                    allActionsSelected
                                        ? "Set actions to none"
                                        : "Set all actions"
                                }
                                onClick={() =>
                                    toggleAllNoneForEntity(
                                        entityTypeId,
                                        "actionMask",
                                        actionOptions,
                                        row.actionMask,
                                    )
                                }
                                style={{
                                    width: "22px",
                                    height: "22px",
                                    fontSize: "0.65rem",
                                    transition:
                                        "transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease",
                                }}
                                onMouseEnter={(event) => {
                                    event.currentTarget.style.transform =
                                        "translateY(-1px)";
                                    event.currentTarget.style.boxShadow =
                                        "0 0.125rem 0.25rem rgba(0, 0, 0, 0.2)";
                                }}
                                onMouseLeave={(event) => {
                                    event.currentTarget.style.transform =
                                        "translateY(0)";
                                    event.currentTarget.style.boxShadow =
                                        "none";
                                }}
                            >
                                <i className="fas fa-check-double" />
                            </button>
                        </div>
                    </div>
                );
            },
        },
        {
            name: "Channels",
            grow: 2.1,
            cell: (row) => {
                const entityTypeId = Number(row.entityType.id);
                const allChannelsSelected = isAllSelected(
                    row.channelMask || 0,
                    channelOptions,
                );

                return (
                    <div className="d-flex flex-column gap-1 py-1">
                        <div className="d-flex flex-wrap align-items-center gap-3">
                            {channelOptions.map((channel) => (
                                <div key={`${entityTypeId}-${channel.key}`}>
                                    <ToggleSwitch
                                        id={`channel-${entityTypeId}-${channel.key}`}
                                        checked={isBitSet(
                                            row.channelMask,
                                            channel.bitLocation,
                                        )}
                                        onChange={() =>
                                            toggleMaskBit(
                                                entityTypeId,
                                                "channelMask",
                                                channel.bitLocation,
                                            )
                                        }
                                        label={channel.label}
                                    />
                                </div>
                            ))}
                            <button
                                type="button"
                                className={`btn btn-sm rounded-circle d-inline-flex align-items-center justify-content-center ${
                                    allChannelsSelected
                                        ? "btn-primary"
                                        : "btn-dark"
                                }`}
                                title={
                                    allChannelsSelected
                                        ? "Set channels to none"
                                        : "Set all channels"
                                }
                                onClick={() =>
                                    toggleAllNoneForEntity(
                                        entityTypeId,
                                        "channelMask",
                                        channelOptions,
                                        row.channelMask,
                                    )
                                }
                                style={{
                                    width: "22px",
                                    height: "22px",
                                    fontSize: "0.65rem",
                                    transition:
                                        "transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease",
                                }}
                                onMouseEnter={(event) => {
                                    event.currentTarget.style.transform =
                                        "translateY(-1px)";
                                    event.currentTarget.style.boxShadow =
                                        "0 0.125rem 0.25rem rgba(0, 0, 0, 0.2)";
                                }}
                                onMouseLeave={(event) => {
                                    event.currentTarget.style.transform =
                                        "translateY(0)";
                                    event.currentTarget.style.boxShadow =
                                        "none";
                                }}
                            >
                                <i className="fas fa-check-double" />
                            </button>
                        </div>
                    </div>
                );
            },
        },
    ];

    const visibleColumns = columns.filter((_, index) => {
        if (index === 0) return true;
        return showColumns[index - 1];
    });

    const customStyles = {
        headCells: { style: { fontWeight: "bold" } },
        cells: {
            style: {
                padding: "6px 12px",
                overflow: "visible",
                width: "100%",
                minWidth: "0",
                maxWidth: "none",
            },
        },
        table: {
            style: {
                tableLayout: "fixed",
                width: "100%",
            },
        },
        rows: {
            style: {
                width: "100%",
            },
        },
    };

    if (!userId) {
        return (
            <div className="container-fluid pt-3">
                <div className="alert alert-warning mb-0">
                    Unable to load notifications because user context is
                    missing.
                </div>
            </div>
        );
    }

    if (loadingEntityTypes || loadingNotifications) {
        return <EntityLoadingState icon="fa-bullhorn" />;
    }

    if (entityTypesError || notificationsError) {
        return (
            <div className="container-fluid pt-3">
                <div className="alert alert-danger mb-0">
                    {(entityTypesError || notificationsError)?.message ||
                        "Failed to load notification preferences."}
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid">
            <div className="font-weight-bold mb-3">Notifications</div>
            {smsEnabledWithoutPhone && (
                <div className="alert alert-warning col-12">
                    <i className="fa fa-exclamation-triangle me-2"></i>
                    SMS notifications are enabled but your profile has no phone
                    number. Add one under your profile menu (top right) to start
                    receiving them.
                </div>
            )}
            <div className="mb-3">
                <div className="d-flex gap-2 flex-wrap">
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => applyActionBulk(true)}
                        disabled={!hasAnyActionOptions}
                    >
                        All Actions
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => applyActionBulk(false)}
                        disabled={!hasAnyActionOptions}
                    >
                        No Actions
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => applyChannelBulk(true)}
                        disabled={channelOptions.length === 0}
                    >
                        All Channels
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => applyChannelBulk(false)}
                        disabled={channelOptions.length === 0}
                    >
                        No Channels
                    </button>
                </div>
            </div>

            <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="d-flex align-items-center" style={{ width: "50%" }}>
                    <input
                        type="text"
                        className="border rounded p-2 flex-grow-1"
                        placeholder="Search notifications by entity name or code…"
                        value={filterText}
                        onChange={(event) => setFilterText(event.target.value)}
                    />
                    <Dropdown
                        className="mx-3"
                        show={dropdownOpen}
                        onToggle={() => setDropdownOpen(!dropdownOpen)}
                    >
                        <Dropdown.Toggle
                            variant="outline-primary"
                            id="dropdown-notification-columns"
                        >
                            <i className="fa fa-filter me-2"></i>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            {["Actions", "Channels"].map(
                                (columnName, index) => (
                                    <Dropdown.Item key={columnName}>
                                        <label className="d-flex align-items-center">
                                            <input
                                                type="checkbox"
                                                checked={showColumns[index]}
                                                onChange={() =>
                                                    toggleColumnVisibility(
                                                        index,
                                                    )
                                                }
                                                onClick={(event) =>
                                                    event.stopPropagation()
                                                }
                                                className="me-2"
                                            />
                                            {columnName}
                                        </label>
                                    </Dropdown.Item>
                                ),
                            )}
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
                <div>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSave}
                        disabled={isSaving}
                    >
                        {isSaving ? "Saving..." : "Save Changes"}
                    </button>
                </div>
            </div>

            <DataTable
                columns={visibleColumns}
                data={pageRows}
                sortServer
                onSort={(_column, direction) =>
                    setSortDirection(direction === "desc" ? "desc" : "asc")
                }
                highlightOnHover
                striped
                responsive
                customStyles={customStyles}
                noDataComponent={
                    <div className="text-center py-5">
                        <p className="text-muted">
                            No notification entries found
                            {filterText && " matching your search"}
                        </p>
                    </div>
                }
            />

            <Paginator
                page={page}
                pageCount={pageCount}
                pageSize={pageSize}
                total={sortedRows.length}
                onChange={setPage}
            />
        </div>
    );
}
